import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.scss';
import useDomainStore from "../../../stores/domain";
import usePlacesProxy from "../../../../hooks/usePlacesProxy";

const AddressAutocomplete = ({
  id,
  name,
  label,
  placeholder,
  onPlaceSelected,
  prefix,
  suffix,
  touched,
  errored,
  error,
  countryRestriction,
  value,
  onChange,
  onFocus,
  onBlur,
  ...otherProps
}) => {
  const { isKurvDomain } = useDomainStore();
  const [localValue, setLocalValue] = useState(value || '');

  const hasError = errored ? true : false;

  const {
    suggestions,
    showSuggestions,
    activeIndex,
    fetchSuggestions,
    selectPlace,
    clearSuggestions,
    handleKeyDown,
    containerRef,
  } = usePlacesProxy({
    onPlaceSelected,
    options: {
      types: ["geocode"],
      componentRestrictions: countryRestriction
        ? { country: countryRestriction }
        : { country: "us" },
    },
  });

  const inputClassNames = [
    styles.input,
    prefix ? styles.prefixInput : '',
    suffix ? styles.suffixInput : '',
    touched && hasError ? styles.errorInput : '',
  ].filter(Boolean).join(' ');

  useEffect(() => {
    if (value !== undefined) {
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    fetchSuggestions(newValue);
    if (onChange) {
      onChange(e);
    }
  };

  const handleSuggestionClick = async (prediction) => {
    clearSuggestions();
    await selectPlace(prediction);
  };

  return (
    <div className={`${styles.formGroup} ${isKurvDomain ? styles.kurv_theme : ""}`} ref={containerRef}>
      {label && <label htmlFor={id} className={styles.label}>{label}</label>}
      <div className={styles.inputWrapper}>
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <input
          {...otherProps}
          id={id}
          name={name}
          className={inputClassNames}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          value={localValue}
          onChange={handleChange}
          autoComplete="off"
        />
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className={styles.suggestionsDropdown}>
          {suggestions.map((prediction, index) => (
            <li
              key={prediction.place_id}
              className={`${styles.suggestionItem} ${index === activeIndex ? styles.suggestionItemActive : ''}`}
              onMouseDown={() => handleSuggestionClick(prediction)}
            >
              <span className={styles.suggestionMain}>
                {prediction.structured_formatting?.main_text}
              </span>
              <span className={styles.suggestionSecondary}>
                {prediction.structured_formatting?.secondary_text}
              </span>
            </li>
          ))}
        </ul>
      )}

      {touched && hasError && error && (
        <span className={styles.error}>{error}</span>
      )}
    </div>
  );
};

AddressAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  onPlaceSelected: PropTypes.func,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  touched: PropTypes.bool,
  errored: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  error: PropTypes.string,
  value: PropTypes.string,
  countryRestriction: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};

AddressAutocomplete.defaultProps = {
  label: '',
  placeholder: 'Enter an address',
  touched: false,
  errored: false,
  error: '',
  value: '',
  countryRestriction: ''
};

export default AddressAutocomplete;
